// New Plan Year Wizard — Step 10.5 (Dave 2026-02-16).
//
// Three-step flow:
//   1. Year picker + carry-forward preview text
//   2. Element Diff — first 5 years of the NEW plan (added 2026-02-16 pt 2)
//   3. Success + counts
//
// Clones the current active PlanYear forward for the AGM. Deep-copies
// every element line item (per-element attributes with RUL decremented
// and cost inflated) plus the Baseline scenario and its element
// overrides. Prior plan year → archived; new plan year → active. Any
// Association Admin, Firm Admin, or Platform Admin can trigger this
// from the Association tab.
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Calendar, ArrowRight, ChevronRight, ChevronLeft } from "lucide-react";
import { fmtCurrency } from "../lib/formatters";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewPlanYearWizard({ association, onClose, onCreated }) {
  const aid = association?.aid;
  const [priorYears, setPriorYears] = useState([]);
  // 2026-10-08 (Dave) — gate the "No active plan year found" banner
  // so it doesn't flash on the first paint before the plan-years
  // fetch has had a chance to populate `priorYears`.
  const [priorYearsLoaded, setPriorYearsLoaded] = useState(false);
  const [year, setYear] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  // Step machine: "pick" → "diff" → "done".
  const [step, setStep] = useState("pick");

  // Diff preview loaded on entry to step 2.
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // 2026-02-23 (Dave) — Projected reserve balance at 12/31/(new_year-1).
  // Stored as a formatted string so the input can show "$1,234,567"
  // while the user types; parsed to a number on submit.
  const [balanceStr, setBalanceStr] = useState("");
  const balanceNum = (() => {
    const digits = (balanceStr || "").replace(/[^0-9]/g, "");
    return digits ? Number(digits) : null;
  })();
  const formatBalanceInput = (raw) => {
    const digits = (raw || "").replace(/[^0-9]/g, "");
    if (!digits) return "";
    return "$" + Number(digits).toLocaleString("en-US");
  };

  useEffect(() => {
    if (!aid) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/associations/${aid}/plan-years`);
        const py = res.data?.plan_years || [];
        setPriorYears(py);
        const activeYear = py.find((p) => p.status === "active")?.year;
        if (activeYear) setYear(String(activeYear + 1));
      } catch (e) {
        setErr(e?.response?.data?.detail || "Failed to load plan years.");
      } finally {
        setPriorYearsLoaded(true);
      }
    })();
  }, [aid]);

  const activePY = priorYears.find((p) => p.status === "active");

  const loadPreview = async () => {
    setPreviewLoading(true);
    setErr("");
    setPreview(null);
    try {
      const yr = parseInt(year, 10);
      const res = await axios.get(
        `${API}/associations/${aid}/plan-years/preview?year=${yr}`,
      );
      setPreview(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load element diff.");
      // Bounce back to step 1 so the user can fix their year.
      setStep("pick");
    } finally {
      setPreviewLoading(false);
    }
  };

  const goToDiff = async () => {
    setStep("diff");
    await loadPreview();
  };

  const submit = async () => {
    setBusy(true);
    setErr("");
    try {
      const yr = parseInt(year, 10);
      const res = await axios.post(`${API}/associations/${aid}/plan-years`, {
        year: yr,
        beginning_balance: balanceNum,
      });
      setResult(res.data);
      setStep("done");
      onCreated?.(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to create new plan year.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(15,42,68,0.55)" }}
      onClick={busy || previewLoading ? undefined : onClose}
      data-testid="new-plan-year-wizard"
    >
      <div
        className="bg-white border-2 border-navy shadow-2xl rounded-[8px] w-[640px] max-w-[95vw] max-h-[86vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-navy text-white px-4 py-3 flex items-center gap-2 rounded-t-[6px]">
          <Calendar size={16} />
          <div className="font-semibold text-sm flex-1">Start a New Plan Year</div>
          <div className="text-[10.5px] uppercase tracking-wider text-white/70">
            {step === "pick" && "Step 1 of 3 · Pick year"}
            {step === "diff" && "Step 2 of 3 · Element diff"}
            {step === "done" && "Step 3 of 3 · Complete"}
          </div>
        </div>

        {step === "done" && result && (
          <div className="p-5">
            <div className="text-navy font-semibold text-[15px] mb-2">
              Plan year {result.year} is now active.
            </div>
            <ul className="text-[12.5px] text-slate list-disc pl-5 space-y-1">
              <li>{result.line_items_copied} element line items carried forward</li>
              <li>
                {result.scenarios_copied ?? 1} scenario
                {(result.scenarios_copied ?? 1) === 1 ? "" : "s"} carried
                forward (Element Baseline plus every what-if)
              </li>
              <li>{result.overrides_copied} scenario override(s) carried forward</li>
              <li>
                Previous plan year <b>{result.prior_year}</b> archived
              </li>
            </ul>
            <div className="flex justify-end mt-4">
              <button
                onClick={onClose}
                className="px-4 py-1.5 bg-navy text-white text-[12px] font-semibold rounded-[3px]"
                style={{ boxShadow: "0 0 0 2px #EA6A47" }}
                data-testid="new-plan-year-done"
              >
                Done
              </button>
            </div>
          </div>
        )}

        {step === "pick" && (
          <>
            <div className="p-5 overflow-auto">
              {err && (
                <div
                  className="mb-3 px-3 py-2 border border-coral bg-[#FEF5F2] text-coral text-[12px]"
                  data-testid="new-plan-year-error"
                >
                  {err}
                </div>
              )}

              {activePY ? (
                <div className="mb-4 p-3 bg-[#F5F9FE] border border-rule rounded-[4px] text-[12.5px]">
                  <div className="flex items-center gap-2 text-navy font-semibold mb-1">
                    <span>Plan Year {activePY.year}</span>
                    <ArrowRight size={12} className="text-slate" />
                    <span>Plan Year {year || "\u2026"}</span>
                  </div>
                  <div className="text-slate">
                    Every element in the current plan year is cloned forward.
                    Costs inflate by the baseline inflation rate; remaining
                    useful life decrements by one year (or the difference if
                    you skip years). The prior plan year is archived and the
                    new one becomes active immediately.
                  </div>
                </div>
              ) : priorYearsLoaded ? (
                <div className="mb-4 px-3 py-2 border border-coral bg-[#FEF5F2] text-coral text-[12px]">
                  No active plan year found. Ask a Platform Admin to seed a
                  baseline before starting a new plan year.
                </div>
              ) : null}

              <label className="text-[10.5px] text-slate uppercase tracking-wider block mb-1">
                New Plan Year
              </label>
              <input
                type="number"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                disabled={busy || !activePY}
                className="w-40 px-2 py-1 border border-rule rounded-[3px] text-[13px] font-mono focus:border-navy outline-none"
                data-testid="new-plan-year-input"
              />
              <div className="text-[11px] text-slate mt-1">
                Must be greater than the current active year (
                {activePY?.year ?? "\u2014"}).
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-4 py-3 bg-navy-wash border-t border-rule rounded-b-[6px]">
              <button
                onClick={onClose}
                disabled={busy}
                className="px-3 py-1.5 text-[12px] text-slate hover:text-navy disabled:opacity-40"
                data-testid="new-plan-year-cancel"
              >
                Cancel
              </button>
              <button
                onClick={goToDiff}
                disabled={busy || !activePY || !year}
                className="px-4 py-1.5 bg-navy text-white text-[12px] font-semibold rounded-[3px] disabled:opacity-40 inline-flex items-center gap-1"
                style={{ boxShadow: busy ? "none" : "0 0 0 2px #EA6A47" }}
                data-testid="new-plan-year-preview"
              >
                Preview Element Difference <ChevronRight size={14} />
              </button>
            </div>
          </>
        )}

        {step === "diff" && (
          <>
            <div
              className="p-5 overflow-auto flex-1"
              data-testid="new-plan-year-diff-panel"
            >
              {err && (
                <div className="mb-3 px-3 py-2 border border-coral bg-[#FEF5F2] text-coral text-[12px]">
                  {err}
                </div>
              )}

              {previewLoading && (
                <div className="text-slate italic text-[13px]">
                  Loading element diff&hellip;
                </div>
              )}

              {preview && (
                <>
                  <div className="text-navy font-semibold text-[14px] mb-1">
                    First 5 years of Plan Year {preview.new_year}
                  </div>
                  <div className="text-[12px] text-slate mb-3 leading-relaxed">
                    Compared to Plan Year <b>{preview.prior_year}</b>. Each
                    element&apos;s remaining useful life drops by{" "}
                    <b>{preview.year_delta}</b> year
                    {preview.year_delta === 1 ? "" : "s"}, and every cost is
                    inflated at the baseline rate of{" "}
                    <b>{(preview.inflation_rate * 100).toFixed(1)}%</b> per
                    year. Every scheduled replacement in the new plan is
                    listed below &mdash; scroll to see later years. The
                    engine will schedule replacements for the remaining{" "}
                    <b>
                      {preview.counts.total_line_items -
                        preview.counts.in_horizon}
                    </b>{" "}
                    element(s) further out.
                  </div>

                  {/* 2026-02-17 (Dave) — "What carries forward?" callout.
                     Plain-English receipt of exactly what the clone will
                     copy so the AGM room has a check they can sign off
                     on before hitting Confirm. Counts come straight from
                     the preview endpoint's `counts.carry_forward` block
                     and match the response payload of POST /plan-years
                     one-to-one. */}
                  {preview.counts.carry_forward && (
                    <div
                      className="mb-3 border-2 border-navy rounded-[4px] overflow-hidden"
                      data-testid="carry-forward-callout"
                    >
                      <div className="bg-navy text-white px-3 py-1 text-[10.5px] uppercase tracking-[0.14em] font-bold">
                        What carries forward
                      </div>
                      <div className="grid grid-cols-3 divide-x divide-rule bg-[#F5F9FE]">
                        <div className="px-3 py-2 text-center">
                          <div
                            className="text-navy font-mono font-bold text-[18px] leading-none"
                            data-testid="carry-forward-elements"
                          >
                            {preview.counts.carry_forward.line_items}
                          </div>
                          <div className="text-[10.5px] uppercase tracking-wider text-slate mt-1">
                            Elements
                          </div>
                        </div>
                        <div className="px-3 py-2 text-center">
                          <div className="text-navy font-mono font-bold text-[18px] leading-none"
                            data-testid="carry-forward-scenarios"
                          >
                            {preview.counts.carry_forward.scenarios_total}
                          </div>
                          <div className="text-[10.5px] uppercase tracking-wider text-slate mt-1">
                            Scenarios
                          </div>
                          <div className="text-[10px] text-slate/70 mt-0.5">
                            Element Baseline
                            {preview.counts.carry_forward
                              .scenarios_nonbaseline > 0 && (
                              <>
                                {" + "}
                                {
                                  preview.counts.carry_forward
                                    .scenarios_nonbaseline
                                }{" "}
                                what-if
                                {preview.counts.carry_forward
                                  .scenarios_nonbaseline === 1
                                  ? ""
                                  : "s"}
                              </>
                            )}
                          </div>
                        </div>
                        <div className="px-3 py-2 text-center">
                          <div
                            className="text-navy font-mono font-bold text-[18px] leading-none"
                            data-testid="carry-forward-overrides"
                          >
                            {preview.counts.carry_forward.overrides}
                          </div>
                          <div className="text-[10.5px] uppercase tracking-wider text-slate mt-1">
                            Overrides
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 2026-10-08 (Dave) — Horizon expanded from 5 to
                      30 years; wrap the stack in a bounded scrollable
                      container so the modal stays a predictable size
                      regardless of how many replacement years fire.
                      max-h-[60vh] keeps the Cancel / Commit footer
                      visible without the user needing to scroll the
                      whole page. */}
                  <div
                    className="border-2 border-navy rounded-[4px] overflow-hidden"
                    data-testid="diff-years-scroller"
                  >
                    <div className="max-h-[60vh] overflow-y-auto">
                      {preview.years_in_new_plan.map((yr) => (
                      <div
                        key={yr.year_in_new_plan}
                        data-testid={`diff-year-${yr.year_in_new_plan}`}
                        className="border-b border-rule last:border-b-0"
                      >
                        <div className="bg-navy text-white px-3 py-1.5 flex items-baseline justify-between text-[12.5px]">
                          <div className="flex items-baseline gap-2">
                            <span className="font-semibold">
                              Year {yr.year_in_new_plan}
                            </span>
                            <span className="opacity-75">
                              &middot; Calendar {yr.calendar_year}
                            </span>
                            <span className="opacity-75">
                              &middot;{" "}
                              {yr.count === 0
                                ? "no scheduled replacements"
                                : `${yr.count} element${yr.count === 1 ? "" : "s"}`}
                            </span>
                          </div>
                          {yr.count > 0 && (
                            <div className="font-mono">
                              {fmtCurrency(yr.total_extended_cost)}
                            </div>
                          )}
                        </div>
                        {yr.count > 0 && (
                          <table className="w-full text-[12px]" data-testid={`diff-year-table-${yr.year_in_new_plan}`}>
                            <thead className="text-[10.5px] text-slate uppercase tracking-wider">
                              <tr>
                                <th className="text-left px-3 py-1 border-b border-rule font-semibold">
                                  Element
                                </th>
                                <th
                                  className="text-right px-3 py-1 border-b border-rule font-semibold"
                                  style={{ width: 160 }}
                                >
                                  Cost change
                                </th>
                                <th
                                  className="text-right px-3 py-1 border-b border-rule font-semibold"
                                  style={{ width: 130 }}
                                >
                                  Extended
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {yr.elements.map((el, i) => {
                                // Cost delta (unit price, before quantity).
                                // Elements with a zero prior cost (rare —
                                // legacy seeded rows without a baseline
                                // price) render as a straight new price
                                // instead of an infinite % change.
                                const oldC = Number(el.current_cost_old) || 0;
                                const newC = Number(el.current_cost_new) || 0;
                                const pct = oldC > 0
                                  ? ((newC / oldC) - 1) * 100
                                  : null;
                                return (
                                  <tr
                                    key={el.element_id}
                                    style={{
                                      background: i % 2 === 1 ? "var(--u-sky-wash)" : undefined,
                                    }}
                                    data-testid={`diff-row-${el.element_id}`}
                                  >
                                    <td className="px-3 py-1 border-b border-rule">
                                      {el.name}
                                    </td>
                                    <td
                                      className="px-3 py-1 text-right font-mono border-b border-rule whitespace-nowrap"
                                      style={{ width: 160, fontSize: 11 }}
                                    >
                                      {oldC > 0 ? (
                                        <>
                                          {fmtCurrency(oldC)}
                                          <span className="mx-1 text-slate">&rarr;</span>
                                          {fmtCurrency(newC)}
                                          <span
                                            className="ml-1"
                                            style={{
                                              color: pct > 0 ? "#EA6A47" : "var(--u-slate)",
                                              fontSize: 10.5,
                                            }}
                                            data-testid={`diff-pct-${el.element_id}`}
                                          >
                                            ({pct >= 0 ? "+" : ""}{pct.toFixed(1)}%)
                                          </span>
                                        </>
                                      ) : (
                                        <span className="text-slate">
                                          {fmtCurrency(newC)}
                                          <span className="ml-1 text-[10.5px]">(new)</span>
                                        </span>
                                      )}
                                    </td>
                                    <td
                                      className="px-3 py-1 text-right font-mono border-b border-rule"
                                      style={{ width: 130 }}
                                    >
                                      {fmtCurrency(el.extended_cost_new)}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </div>
                    ))}
                    </div>
                  </div>

                  <div className="text-[11.5px] text-slate mt-3 leading-relaxed">
                    This is a preview only. Nothing has been changed yet.
                    Click <b>Confirm &amp; Create</b> below to archive Plan
                    Year <b>{preview.prior_year}</b> and activate Plan Year{" "}
                    <b>{preview.new_year}</b>.
                  </div>

                  {/* 2026-02-23 (Dave) — Projected reserve balance input.
                      Fills the Opening Balance of every scenario cloned
                      into the new plan year and drives the Associations
                      tab left panel display. Required to commission. */}
                  <div
                    className="mt-4 p-3 border border-navy bg-navy-wash rounded-[4px]"
                    data-testid="new-plan-year-balance-block"
                  >
                    <label
                      htmlFor="npy-balance-input"
                      className="block text-[11.5px] font-semibold text-navy leading-snug mb-1.5"
                    >
                      Projected Reserve Balance at 12/31/{preview.new_year - 1}
                    </label>
                    <input
                      id="npy-balance-input"
                      data-testid="new-plan-year-balance-input"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="$0"
                      value={balanceStr}
                      onChange={(e) => setBalanceStr(formatBalanceInput(e.target.value))}
                      disabled={busy || previewLoading}
                      className="w-56 px-2 py-1 text-[13px] font-mono tabular-nums text-ink border border-rule bg-white rounded-[3px] focus:outline-none focus:border-navy"
                    />
                    <div className="text-[10.5px] text-slate mt-1.5 leading-snug">
                      Fills the Opening Balance for all scenarios in Plan
                      Year {preview.new_year} and appears in the
                      Association tab left panel.
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 px-4 py-3 bg-navy-wash border-t border-rule rounded-b-[6px]">
              <button
                onClick={() => setStep("pick")}
                disabled={busy || previewLoading}
                className="px-3 py-1.5 text-[12px] text-slate hover:text-navy disabled:opacity-40 inline-flex items-center gap-1"
                data-testid="new-plan-year-back"
              >
                <ChevronLeft size={14} /> Back
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={onClose}
                  disabled={busy || previewLoading}
                  className="px-3 py-1.5 text-[12px] text-slate hover:text-navy disabled:opacity-40"
                  data-testid="new-plan-year-cancel-diff"
                >
                  Cancel
                </button>
                <button
                  onClick={submit}
                  disabled={busy || previewLoading || !preview || balanceNum === null || balanceNum < 0}
                  title={balanceNum === null ? "Enter the projected 12/31 reserve balance to enable." : undefined}
                  className="px-4 py-1.5 bg-navy text-white text-[12px] font-semibold rounded-[3px] disabled:opacity-40"
                  style={{ boxShadow: (busy || previewLoading || balanceNum === null) ? "none" : "0 0 0 2px #EA6A47" }}
                  data-testid="new-plan-year-confirm"
                >
                  {busy ? "Creating\u2026" : "Confirm & Create"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
